import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  ElementFormatType,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  Spread,
} from 'lexical';
import { BlockWithAlignableContents } from '@lexical/react/LexicalBlockWithAlignableContents';
import {
  DecoratorBlockNode,
  SerializedDecoratorBlockNode,
} from '@lexical/react/LexicalDecoratorBlockNode';
import {
  Card,
  getThemeColor,
  Group,
  Image,
  Paper,
  Stack,
  Text,
  UnstyledButton,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';

// Bookmark Component
function BookmarkComponent({
  className,
  format,
  nodeKey,
  url,
  title,
  description,
  favicon,
  cover,
}) {
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();

  return (
    <BlockWithAlignableContents className={className} format={format} nodeKey={nodeKey}>
      <UnstyledButton component="a" href={url} target="_blank" rel="noopener noreferrer">
        <Paper
          shadow="md"
          p="lg"
          radius="md"
          mb="md"
          bg="transparent"
          withBorder
          style={{
            borderColor:
              colorScheme === 'light'
                ? getThemeColor('dark.7', theme)
                : getThemeColor('white', theme),
          }}
        >
          <Stack gap="md" mb="md">
            <Text fw={500}>{title}</Text>
            <Text>{description}</Text>
            <Group>
              <Image src={favicon} alt="Favicon" width={24} height={24} />
              <Text truncate="end" style={{ flex: 1 }}>
                {url}
              </Text>
            </Group>
          </Stack>
          {cover && (
            <Image
              src={cover}
              alt="Cover"
              style={{ objectFit: 'cover', width: '100%', borderRadius: '4px' }}
            />
          )}
        </Paper>
      </UnstyledButton>
    </BlockWithAlignableContents>
  );
}

export type SerializedBookmarkNode = Spread<
  {
    url: string;
    title: string;
    description: string;
    favicon: string;
    cover: string;
  },
  SerializedDecoratorBlockNode
>;

function $convertBookmarkElement(domNode: HTMLElement): null | DOMConversionOutput {
  const url = domNode.getAttribute('data-url');
  const title = domNode.getAttribute('data-title');
  const description = domNode.getAttribute('data-description');
  const favicon = domNode.getAttribute('data-favicon');
  const cover = domNode.getAttribute('data-cover');
  if (url && title) {
    const node = $createBookmarkNode({ url, title, description, favicon, cover });
    return { node };
  }
  return null;
}

export class BookmarkNode extends DecoratorBlockNode {
  __url: string;
  __title: string;
  __description: string;
  __favicon: string;
  __cover: string;

  static getType(): string {
    return 'bookmark';
  }

  static clone(node: BookmarkNode): BookmarkNode {
    return new BookmarkNode(
      node.__url,
      node.__title,
      node.__description,
      node.__favicon,
      node.__cover,
      node.__format,
      node.__key
    );
  }

  static importJSON(serializedNode: SerializedBookmarkNode): BookmarkNode {
    const { url, title, description, favicon, cover } = serializedNode;
    return new BookmarkNode(url, title, description, favicon, cover);
  }

  exportJSON(): SerializedBookmarkNode {
    return {
      ...super.exportJSON(),
      type: 'bookmark',
      version: 1,
      url: this.__url,
      title: this.__title,
      description: this.__description,
      favicon: this.__favicon,
      cover: this.__cover,
    };
  }

  constructor(
    url: string,
    title: string,
    description: string,
    favicon: string,
    cover: string,
    format?: ElementFormatType,
    key?: NodeKey
  ) {
    super(format, key);
    this.__url = url;
    this.__title = title;
    this.__description = description;
    this.__favicon = favicon;
    this.__cover = cover;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('div');
    element.setAttribute('data-url', this.__url);
    element.setAttribute('data-title', this.__title);
    element.setAttribute('data-description', this.__description);
    element.setAttribute('data-favicon', this.__favicon);
    element.setAttribute('data-cover', this.__cover);
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-url')) {
          return null;
        }
        return { conversion: $convertBookmarkElement, priority: 1 };
      },
    };
  }

  updateDOM(): false {
    return false;
  }

  decorate(_editor: LexicalEditor, config: EditorConfig): JSX.Element {
    const embedBlockTheme = config.theme.embedBlock || {};
    const className = {
      base: embedBlockTheme.base || '',
      focus: embedBlockTheme.focus || '',
    };
    return (
      <BookmarkComponent
        className={className}
        format={this.__format}
        nodeKey={this.getKey()}
        url={this.__url}
        title={this.__title}
        description={this.__description}
        favicon={this.__favicon}
        cover={this.__cover}
      />
    );
  }
}

export function $createBookmarkNode({
  url,
  title,
  description,
  favicon,
  cover,
}: SerializedBookmarkNode): BookmarkNode {
  return new BookmarkNode(url, title, description, favicon, cover);
}

export function $isBookmarkNode(
  node: BookmarkNode | LexicalNode | null | undefined
): node is BookmarkNode {
  return node instanceof BookmarkNode;
}
